import { isDemoMode, isLegacyAuthMode } from "./env";
import { configureSupabase } from "./supabase";

const RUNTIME_CONFIG_PATH = "/api/runtime-config";
const RUNTIME_CONFIG_TIMEOUT_MS = 5_000;

type RuntimeConfigPayload = {
  schema_version: number;
  supabase_url: string;
  supabase_publishable_key: string;
};

export async function configureRuntime(): Promise<void> {
  if (
    isDemoMode() ||
    isLegacyAuthMode()
  ) {
    return;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    RUNTIME_CONFIG_TIMEOUT_MS,
  );
  try {
    const response = await fetch(RUNTIME_CONFIG_PATH, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error("runtime config request failed");
    }
    const payload = parseRuntimeConfig(await response.json());
    configureSupabase({
      url: payload.supabase_url,
      publishableKey: payload.supabase_publishable_key,
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

function parseRuntimeConfig(value: unknown): RuntimeConfigPayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("runtime config response is malformed");
  }
  const payload = value as Record<string, unknown>;
  if (
    payload.schema_version !== 1 ||
    typeof payload.supabase_url !== "string" ||
    !isHttpUrl(payload.supabase_url) ||
    typeof payload.supabase_publishable_key !== "string" ||
    !isBrowserSafeSupabaseKey(payload.supabase_publishable_key)
  ) {
    throw new Error("runtime config response is unsafe");
  }
  return payload as RuntimeConfigPayload;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      url.hostname.length > 0 &&
      url.username === "" &&
      url.password === "" &&
      (url.pathname === "" || url.pathname === "/") &&
      url.search === "" &&
      url.hash === ""
    );
  } catch {
    return false;
  }
}

function isBrowserSafeSupabaseKey(value: string): boolean {
  if (value.startsWith("sb_publishable_")) {
    return value.length > "sb_publishable_".length;
  }
  if (value.startsWith("sb_secret_")) return false;
  const parts = value.split(".");
  if (parts.length !== 3) return false;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(window.atob(padded)) as unknown;
    return (
      Boolean(payload) &&
      typeof payload === "object" &&
      !Array.isArray(payload) &&
      (payload as Record<string, unknown>).role === "anon"
    );
  } catch {
    return false;
  }
}
